import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '@/components/Logo';
import ThemeToggle from '@/components/ThemeToggle';
import SEO from '@/components/SEO';

const CookiePage = () => (
  <div className="min-h-screen bg-background">
    <SEO title="Cookie Policy" description="How Poettik uses cookies to improve your experience, and how to manage your preferences." />
    <nav className="border-b border-border bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/"><Logo /></Link>
        <ThemeToggle />
      </div>
    </nav>
    <main className="max-w-3xl mx-auto px-4 py-12 prose prose-sm dark:prose-invert">
      <h1>Cookie Policy</h1>
      <p className="text-muted-foreground">Last updated: June 2026</p>

      <h2>1. What Are Cookies &amp; Similar Technologies</h2>
      <p>Cookies are small text files stored on your device when you visit a website. Websites also use similar technologies, such as browser local storage, for the same purposes. In practice Poettik relies mainly on your browser's local storage rather than traditional cookies, but for simplicity we refer to all of these as "cookies" throughout this policy. They help the site remember your preferences and work properly.</p>

      <h2>2. Cookies &amp; Similar Technologies We Use</h2>
      <p>Most of the items below are stored in your browser's local storage on your own device, not sent to our servers as traditional cookies.</p>

      <h3>Essential Cookies</h3>
      <p>These are necessary for the website to function and cannot be disabled.</p>
      <ul>
        <li><strong>Authentication token:</strong> Keeps you signed in during your session.</li>
        <li><strong>Theme preference:</strong> Remembers your light/dark mode choice.</li>
        <li><strong>Cookie consent:</strong> Records your cookie preference.</li>
      </ul>

      <h3>Functional Cookies</h3>
      <ul>
        <li><strong>Saved ideas:</strong> Stores your bookmarked content ideas locally.</li>
        <li><strong>Region preference:</strong> Remembers your UK/USA content style choice.</li>
        <li><strong>Referral codes:</strong> Tracks affiliate referral links.</li>
        <li><strong>Free tool device ID:</strong> A random identifier stored on your device to apply fair-use limits to our free viral video transcriber. It is not linked to your identity unless you provide your email.</li>
      </ul>

      <h3>Marketing &amp; Advertising</h3>
      <p>With your consent, we use the <strong>Meta (Facebook) Pixel</strong> to measure the performance of our advertising and to understand which pages and actions (such as signing up or subscribing) result from our ads. The Meta Pixel is a tracking technology provided by Meta Platforms, Inc.</p>
      <ul>
        <li>The pixel <strong>only loads after you select "Accept"</strong> on our cookie banner. If you choose "Reject non-essential", it is never loaded.</li>
        <li>It may set Meta cookies and share limited event data (for example that a page was viewed or a subscription completed) with Meta.</li>
        <li>You can withdraw consent at any time by clearing your browser storage, and you can manage ad personalisation in your <a href="https://www.facebook.com/settings" target="_blank" rel="noopener noreferrer" className="text-primary">Facebook settings</a>.</li>
      </ul>
      <p>We do not currently use any other analytics or tracking cookies. If we add more in the future, we will update this policy first.</p>

      <h2>3. Managing Cookies</h2>
      <p>You can control cookies through your browser settings. Note that disabling essential cookies may affect the functionality of our website.</p>
      <p>Most browsers allow you to:</p>
      <ul>
        <li>View what cookies are stored and delete them individually.</li>
        <li>Block third-party cookies.</li>
        <li>Block cookies from specific websites.</li>
        <li>Block all cookies.</li>
        <li>Delete all cookies when you close your browser.</li>
      </ul>

      <h2>4. Third-Party Cookies</h2>
      <ul>
        <li><strong>Stripe:</strong> Used for secure payment processing.</li>
        <li><strong>Meta (Facebook) Pixel:</strong> Loaded only with your consent, to measure our advertising (see "Marketing &amp; Advertising" above).</li>
      </ul>

      <h2>5. Changes to This Policy</h2>
      <p>We may update this cookie policy from time to time. Any changes will be posted on this page.</p>

      <h2>6. Contact Us</h2>
      <p>If you have questions about our use of cookies, contact us at <a href="mailto:hey@poettik.com" className="text-primary">hey@poettik.com</a>.</p>
    </main>
  </div>
);

export default CookiePage;
